/**
 * backtest_page_v3.js — DAS-105 Phase 03: Backtest 시안 매칭
 *
 * 매핑: BacktestDesktopV2 + BacktestMobileV2 (backtest.jsx)
 * UI: Data Ops 허브
 *   - 데스크톱: Hero KPI 스트립 + 파이프라인 시각화(1.2fr/1fr) +
 *     이벤트 스트림(1fr)/로그 테이블(1.2fr) 2-col
 *   - 모바일: Hero KPI + 수집기 2-col + 이벤트 pill 필터 스택
 *
 * 데이터 소스:
 *   - services/infraApi.js: fetchInfraStatus, fetchInfraErrors, fetchInfraErrorStats
 *
 * BE gap (DAS-65-G1): 파이프라인 단계(NORMALIZER/STORAGE) BE 미지원
 *   → adapter active 여부로 단순 매핑.
 *   // TODO(DAS-65-G1): 단계별 상태 BE 확장 필요
 *
 * silver 토큰 전용. JetBrains Mono 유지. 하드코딩 색상(#hex, rgba) 금지.
 */

import React from 'react';
import NavBar from '../common/nav_bar';
import MaintenanceBanner from '../common/maintenance_banner';
import ScrollToTopButton from '../common/scroll_to_top_button';
import { fetchInfraStatus, fetchInfraErrors, fetchInfraErrorStats } from '../../services/infraApi';
import { requireLoginOrThrow } from '../../utils/auth';
import { getNavCollapsed, setNavCollapsed } from '../../utils/navState';
import { isMobileViewport } from '../../utils/responsive';
import { StatusDot, KpiLabel, V2Icon, V2Badge, Icon } from '../common/claude_primitives';
import { getThemeColors } from '../../utils/themeColors';
import {
    PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
} from 'recharts';

/* ── 헬퍼 ─────────────────────────────────────────────────── */
function formatRelativeTime(isoString) {
    if (!isoString) return '—';
    const diff = Math.floor((Date.now() - new Date(isoString).getTime()) / 1000);
    if (diff < 10) return '방금 전';
    if (diff < 60) return `${diff}초 전`;
    if (diff < 3600) return `${Math.floor(diff / 60)}분 전`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}시간 전`;
    return `${Math.floor(diff / 86400)}일 전`;
}

function formatUptime(seconds) {
    if (!seconds && seconds !== 0) return '—';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    if (h > 0) return `${h}h ${m}m ${s}s`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
}

/* ── 스테이지 레이블 공통 스타일 ───────────────────────────── */
function stageLabelStyle() {
    return {
        fontSize: 9,
        letterSpacing: '0.14em',
        color: 'var(--color-content-muted)',
        fontFamily: 'JetBrains Mono, monospace',
        textAlign: 'center',
        textTransform: 'uppercase',
        marginBottom: 6,
    };
}

/* ── 어댑터 카드 (파이프라인 SOURCE 단계) ─────────────────── */
function AdapterCard({ adapter }) {
    const active = adapter.active;
    const latencyMs = adapter.latency_ms ?? null;
    const checkpoints = adapter.checkpoint_keys ?? 0;
    const lastActive = adapter.last_active_at;
    const stallTone = active ? 'gain' : 'loss';
    const latTone = latencyMs === null ? 'neutral'
        : latencyMs < 100 ? 'gain'
        : latencyMs < 300 ? 'warning'
        : 'loss';

    return (
        <div
            role="listitem"
            aria-label={`${adapter.name || adapter.id} — ${active ? '정상' : '비활성'}`}
            style={{
                padding: 12,
                borderRadius: 10,
                background: 'var(--color-surface-elevated)',
                border: '1px solid var(--color-surface-border)',
                display: 'flex', flexDirection: 'column', gap: 5,
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{
                    fontSize: 12, fontWeight: 600,
                    fontFamily: 'JetBrains Mono, monospace',
                    color: 'var(--color-content)',
                    letterSpacing: '-0.01em',
                }}>
                    {adapter.name || adapter.id}
                </span>
                <StatusDot tone={stallTone} size={7} />
            </div>
            <div style={{
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: 11, color: 'var(--color-content-secondary)',
            }}>
                {formatRelativeTime(lastActive)}
            </div>
            <div style={{
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: 10, color: 'var(--color-content-muted)',
            }}>
                체크포인트 {checkpoints}건
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
                {latencyMs !== null && (
                    <span style={{
                        fontFamily: 'JetBrains Mono, monospace',
                        fontSize: 10, fontWeight: 600,
                        color: `var(--color-${latTone})`,
                        display: 'inline-flex', alignItems: 'center', gap: 3,
                    }}>
                        <span style={{
                            width: 4, height: 4, borderRadius: '50%',
                            background: `var(--color-${latTone})`, flexShrink: 0,
                        }} aria-hidden="true" />
                        {latencyMs}ms
                    </span>
                )}
                <span style={{
                    fontFamily: 'JetBrains Mono, monospace',
                    fontSize: 10, fontWeight: 500,
                    color: `var(--color-${stallTone})`,
                }}>
                    {active ? '정상' : '비활성'}
                </span>
            </div>
        </div>
    );
}

/* ── 파이프라인 단계 박스 (NORMALIZER/STORAGE) ─────────────
 * TODO(DAS-65-G1): 단계별 상태 BE 확장 필요 — 현재는 서비스 전체 active 여부 매핑
 * ─────────────────────────────────────────────────────────── */
function PipelineStageBox({ label, sublabel, tone, iconName }) {
    return (
        <div style={{
            padding: 14,
            borderRadius: 10,
            background: 'var(--color-surface-card-alt)',
            border: '1px solid var(--color-surface-border)',
            textAlign: 'center',
        }}>
            {iconName ? (
                <Icon name={iconName} size={20} aria-hidden="true" />
            ) : (
                <StatusDot tone={tone} size={7} />
            )}
            <div style={{ fontSize: 13, fontWeight: 600, marginTop: 6, color: 'var(--color-content)' }}>
                {label}
            </div>
            <div style={{
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: 10, color: 'var(--color-content-muted)', marginTop: 4,
            }}>
                {sublabel}
            </div>
        </div>
    );
}

/* ── SVG 파이프라인 연결선 ───────────────────────────────── */
function PipelineConnector({ height = 140 }) {
    const h = height;
    const t = Math.round(h * 0.15);
    const m = Math.round(h * 0.5);
    const b = Math.round(h * 0.85);
    return (
        <svg width={36} height={h} aria-hidden="true">
            <path
                d={`M 2 ${t} C 20 ${t}, 20 ${m}, 34 ${m}`}
                stroke="var(--color-surface-border)"
                fill="none" strokeWidth={1.5}
            />
            <path
                d={`M 2 ${m} L 34 ${m}`}
                stroke="var(--color-surface-border)"
                fill="none" strokeWidth={1.5}
            />
            <path
                d={`M 2 ${b} C 20 ${b}, 20 ${m}, 34 ${m}`}
                stroke="var(--color-surface-border)"
                fill="none" strokeWidth={1.5}
            />
        </svg>
    );
}

/* ── 이벤트 로그 행 ──────────────────────────────────────── */
function EventRow({ log }) {
    const lv = (log.level || 'info').toLowerCase();
    const tone = lv === 'error' ? 'loss' : lv === 'warn' || lv === 'warning' ? 'warning' : 'gain';
    const ts = log.timestamp || log.created_at || '';
    const timeStr = ts ? ts.replace('T', ' ').slice(0, 19) : '—';

    // 배지 색상 — 토큰 기반
    const badgeBg = lv === 'error' ? 'var(--color-loss-subtle)'
        : lv === 'warn' || lv === 'warning' ? 'var(--color-warning-subtle)'
        : 'var(--color-surface-elevated)';
    const badgeColor = lv === 'error' ? 'var(--color-loss)'
        : lv === 'warn' || lv === 'warning' ? 'var(--color-warning)'
        : 'var(--color-content-secondary)';

    return (
        <div style={{
            display: 'flex', gap: 10,
            padding: '11px 0',
            borderBottom: '1px solid var(--color-surface-border)',
            alignItems: 'flex-start',
        }}>
            <StatusDot tone={tone} size={7} />
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--color-content)' }}>
                    {log.message || log.msg || '—'}
                </div>
                <div style={{
                    fontFamily: 'JetBrains Mono, monospace',
                    fontSize: 10, color: 'var(--color-content-muted)', marginTop: 2,
                }}>
                    {timeStr}
                    {log.exchange ? ` · ${log.exchange}` : ''}
                    {log.symbol ? ` · ${log.symbol}` : ''}
                </div>
            </div>
            <span
                aria-label={`레벨: ${lv}`}
                style={{
                    background: badgeBg,
                    color: badgeColor,
                    border: `1px solid color-mix(in oklch, ${badgeColor} 30%, transparent)`,
                    padding: '2px 7px',
                    borderRadius: 999,
                    fontSize: 10, fontWeight: 700,
                    fontFamily: 'JetBrains Mono, monospace',
                    textTransform: 'uppercase',
                    flexShrink: 0,
                }}
            >
                {lv}
            </span>
        </div>
    );
}

/* ── 로그 테이블 행 (데스크톱) ───────────────────────────── */
function LogTableRow({ log }) {
    const lv = (log.level || 'info').toLowerCase();
    const ts = log.timestamp || log.created_at || '';
    const timeOnly = ts ? ts.replace('T', ' ').slice(11, 19) : '—';
    const lvColor = lv === 'error' ? 'var(--color-loss)'
        : lv === 'warn' || lv === 'warning' ? 'var(--color-warning)'
        : 'var(--color-content-muted)';

    return (
        <tr style={{ borderBottom: '1px solid var(--color-surface-border)' }}>
            <td style={{
                padding: '8px 4px',
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: 11, color: 'var(--color-content-muted)',
                whiteSpace: 'nowrap',
            }}>
                {timeOnly}
            </td>
            <td style={{ padding: '8px 4px' }}>
                <span style={{
                    fontFamily: 'JetBrains Mono, monospace',
                    fontSize: 10, fontWeight: 700,
                    color: lvColor,
                    textTransform: 'uppercase',
                }}>
                    {lv}
                </span>
            </td>
            <td style={{
                padding: '8px 4px',
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: 11, fontWeight: 600,
                color: 'var(--color-content-secondary)',
            }}>
                {log.symbol || '—'}
            </td>
            <td style={{ padding: '8px 4px', fontSize: 12, color: 'var(--color-content-secondary)' }}>
                {log.message || log.msg || '—'}
            </td>
        </tr>
    );
}

/* ── 거래소별 수집 비중 — 시안 V2: 도넛 차트 대신 바 진행률
 * TODO(DAS-105): BE by_exchange 미지원 시 adapters.checkpoint_keys 기반 fallback
 * ─────────────────────────────────────────────────────────── */
function AdapterDistributionChart({ adapters }) {
    const C = getThemeColors();
    // 시안 V2 색 순서: brand, warning, gain, loss, brand, brand
    const COLORS = [C.brand, C.warning, C.gain, C.loss, C.contentSecondary, C.contentMuted];

    if (!adapters || adapters.length === 0) {
        return (
            <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                height: 120, color: 'var(--color-content-muted)', fontSize: 12,
            }}>
                데이터 없음
            </div>
        );
    }

    const total = adapters.reduce((s, a) => s + (a.checkpoint_keys || 1), 0);
    const data = adapters.map((a, i) => ({
        name: a.name || a.id,
        value: a.checkpoint_keys || 1,
        pct: total > 0 ? Math.round((a.checkpoint_keys || 1) / total * 100) : 0,
        color: COLORS[i % COLORS.length],
    }));

    return (
        <div>
            {/* 도넛 차트 — ResponsiveContainer 강제 */}
            <ResponsiveContainer width="100%" height={160}>
                <PieChart>
                    <Pie
                        data={data}
                        cx="50%"
                        cy="50%"
                        innerRadius={42}
                        outerRadius={68}
                        paddingAngle={2}
                        dataKey="value"
                        strokeWidth={0}
                    >
                        {data.map((entry, index) => (
                            <Cell key={index} fill={entry.color} opacity={0.75} />
                        ))}
                    </Pie>
                    <Tooltip
                        formatter={(value, name) => [`${value}건`, name]}
                        contentStyle={{
                            background: 'var(--color-surface-elevated)',
                            border: '1px solid var(--color-surface-border)',
                            borderRadius: 8,
                            fontSize: 11,
                            color: 'var(--color-content)',
                        }}
                    />
                </PieChart>
            </ResponsiveContainer>
            {/* 범례 + 바 진행률 */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
                {data.map((d, i) => (
                    <div key={i}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                            <span style={{ fontSize: 11, color: 'var(--color-content-secondary)' }}>
                                {d.name}
                            </span>
                            <span style={{
                                fontFamily: 'JetBrains Mono, monospace',
                                fontSize: 11, color: 'var(--color-content-secondary)',
                            }}>
                                {d.pct}%
                            </span>
                        </div>
                        <div style={{
                            height: 5,
                            background: 'var(--color-surface-elevated)',
                            borderRadius: 3, overflow: 'hidden',
                        }}>
                            <div style={{
                                height: '100%', width: `${d.pct}%`,
                                background: d.color, opacity: 0.75, borderRadius: 3,
                                transition: 'width .3s ease',
                            }} aria-hidden="true" />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

/* ── Hero KPI 스트립 (데스크톱/모바일 공용) ─────────────── */
function HeroStrip({ infraStatus, errorStats, isMobile }) {
    const adapters = infraStatus?.adapters || [];
    const activeCount = adapters.filter(a => a.active).length;
    const totalCount = adapters.length;
    const allOk = totalCount > 0 && activeCount === totalCount;
    const uptimeSec = infraStatus?.uptime_seconds ?? null;
    const uptimeStr = uptimeSec !== null ? formatUptime(uptimeSec) : (infraStatus?.uptime || '—');
    const totalErrors = errorStats?.total_errors ?? 0;
    const recentRate = errorStats?.recent_rate ?? null;
    const errorRateStr = recentRate !== null ? recentRate.toFixed(2) + '%' : '—';

    if (isMobile) {
        return (
            <div style={{
                padding: 18,
                borderRadius: 14,
                background: 'linear-gradient(135deg, var(--color-surface-card), var(--color-surface-elevated))',
                border: '1px solid var(--color-surface-border)',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                    <StatusDot tone={allOk ? 'gain' : 'warning'} size={8} />
                    <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--color-content)' }}>
                        {allOk ? '모든 파이프라인 정상' : '일부 어댑터 비활성'}
                    </span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                    {[
                        { label: '수집기', value: totalCount > 0 ? `${activeCount}/${totalCount}` : '—' },
                        { label: '처리량', value: '—' /* TODO(DAS-105): BE 처리량 필드 미지원 */ },
                        { label: '에러율', value: errorRateStr },
                    ].map(kpi => (
                        <div key={kpi.label}>
                            <KpiLabel>{kpi.label}</KpiLabel>
                            <div style={{
                                fontFamily: 'JetBrains Mono, monospace',
                                fontWeight: 600, fontSize: 17,
                                color: 'var(--color-content)',
                            }}>
                                {kpi.value}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    // 데스크톱 Hero — 시안 V2: 라디얼 그라디언트 글로우
    return (
        <div style={{
            padding: 22,
            borderRadius: 16,
            background: 'linear-gradient(135deg, var(--color-surface-card), var(--color-surface-elevated))',
            border: '1px solid var(--color-surface-border)',
            position: 'relative', overflow: 'hidden',
        }}>
            <div
                aria-hidden="true"
                style={{
                    position: 'absolute', inset: 0,
                    background: allOk
                        ? 'radial-gradient(circle at 90% 10%, var(--color-gain-subtle), transparent 55%)'
                        : 'radial-gradient(circle at 90% 10%, var(--color-warning-subtle), transparent 55%)',
                    pointerEvents: 'none',
                }}
            />
            <div style={{
                position: 'relative',
                display: 'grid',
                gridTemplateColumns: '1.2fr 1fr 1fr 1fr',
                gap: 28, alignItems: 'center',
            }}>
                {/* 상태 메시지 */}
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                        <StatusDot tone={allOk ? 'gain' : 'warning'} size={9} />
                        <span style={{ fontWeight: 600, fontSize: 16, color: 'var(--color-content)' }}>
                            {allOk ? '모든 파이프라인 정상' : '일부 어댑터 비활성'}
                        </span>
                    </div>
                    <div style={{
                        fontFamily: 'JetBrains Mono, monospace',
                        fontSize: 11, color: 'var(--color-content-muted)',
                        letterSpacing: '0.04em',
                    }}>
                        Uptime {uptimeStr}
                    </div>
                </div>
                {/* KPI 3개 */}
                {[
                    {
                        label: '수집기',
                        value: totalCount > 0 ? `${activeCount} / ${totalCount}` : '—',
                        sub: `정상 · ${totalCount - activeCount} 실패`,
                    },
                    {
                        label: '처리량 / 5분',
                        value: '—', /* TODO(DAS-105): BE 처리량 필드 미지원 */
                        sub: '최근 5분',
                    },
                    {
                        label: '에러율',
                        value: errorRateStr,
                        sub: recentRate !== null ? '최근 5분' : `누적 ${totalErrors.toLocaleString()}건`,
                    },
                ].map(kpi => (
                    <div key={kpi.label}>
                        <KpiLabel>{kpi.label}</KpiLabel>
                        <div style={{
                            fontFamily: 'JetBrains Mono, monospace',
                            fontSize: 26, fontWeight: 600,
                            letterSpacing: '-0.02em',
                            color: 'var(--color-content)',
                        }}>
                            {kpi.value}
                        </div>
                        <div style={{
                            fontFamily: 'JetBrains Mono, monospace',
                            fontSize: 10, color: 'var(--color-content-muted)', marginTop: 4,
                        }}>
                            {kpi.sub}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

/* ── BacktestPageV3 메인 클래스 컴포넌트 ─────────────────── */
class BacktestPageV3 extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            collapsed: getNavCollapsed(),
            isMobile: isMobileViewport(),
            infraStatus: null,
            infraLoading: true,
            infraError: null,
            errors: [],
            errorsLoading: true,
            errorsError: null,
            errorStats: null,
            errorStatsLoading: true,
            logFilter: 'all',
            // 서버 측 필터
            logLevel: '',
            logExchange: '',
            logSearch: '',
            allLogExchanges: [],
            autoRefresh: (() => {
                const stored = localStorage.getItem('backtest_v3_autoRefresh');
                return stored === null ? true : stored === 'true';
            })(),
        };
        this._infraInterval = null;
        this._errorsInterval = null;
        this._resizeListener = null;
        this._searchDebounce = null;
    }

    componentDidMount() {
        // eslint-disable-next-line no-restricted-syntax -- componentDidMount only
        requireLoginOrThrow();
        this.fetchInfra();
        this.fetchErrors();
        this.fetchErrorStats();
        this._infraInterval = setInterval(this.fetchInfra, 5000);
        if (this.state.autoRefresh) {
            this._errorsInterval = setInterval(() => {
                this.fetchErrors();
                this.fetchErrorStats();
            }, 15000);
        }
        this._resizeListener = () => this.setState({ isMobile: isMobileViewport() });
        window.addEventListener('resize', this._resizeListener);
    }

    componentWillUnmount() {
        if (this._infraInterval) clearInterval(this._infraInterval);
        if (this._errorsInterval) clearInterval(this._errorsInterval);
        if (this._resizeListener) window.removeEventListener('resize', this._resizeListener);
        if (this._searchDebounce) clearTimeout(this._searchDebounce);
    }

    fetchInfra = () => {
        fetchInfraStatus()
            .then(res => this.setState({ infraStatus: res?.data, infraLoading: false, infraError: null }))
            .catch(err => this.setState({ infraError: err?.message || '조회 실패', infraLoading: false }));
    };

    fetchErrors = () => {
        const { logLevel, logExchange, logSearch } = this.state;
        const params = { limit: 100 };
        if (logLevel) params.level = logLevel;
        if (logExchange) params.exchange = logExchange;
        if (logSearch) params.search = logSearch;

        fetchInfraErrors(params)
            .then(res => {
                const data = res?.data;
                const results = Array.isArray(data) ? data : (data?.results || data?.errors || []);
                // DAS-116: allLogExchanges 는 모든 fetch(필터 유무 무관)에서 본 거래소를 누적 union.
                // 기존엔 필터 미적용 fetch 에서만 replace 라 새 거래소가 안 보이거나 사라졌음.
                this.setState(prev => {
                    const merged = [...new Set([
                        ...(prev.allLogExchanges || []),
                        ...results.filter(e => e.exchange).map(e => e.exchange),
                    ])].sort();
                    return { errors: results, allLogExchanges: merged, errorsLoading: false, errorsError: null };
                });
            })
            .catch(err => this.setState({ errorsError: err?.message || '조회 실패', errorsLoading: false }));
    };

    fetchErrorStats = () => {
        fetchInfraErrorStats()
            .then(res => this.setState({ errorStats: res?.data, errorStatsLoading: false }))
            .catch(() => this.setState({ errorStatsLoading: false }));
    };

    onLogFilterChange = (field, value) => {
        this.setState({ [field]: value, errorsLoading: true }, () => {
            if (field === 'logSearch') {
                if (this._searchDebounce) clearTimeout(this._searchDebounce);
                this._searchDebounce = setTimeout(this.fetchErrors, 300);
            } else {
                this.fetchErrors();
            }
        });
    };

    toggleAutoRefresh = () => {
        this.setState(prev => {
            const next = !prev.autoRefresh;
            localStorage.setItem('backtest_v3_autoRefresh', String(next));
            return { autoRefresh: next };
        }, () => {
            if (this.state.autoRefresh) {
                if (this._errorsInterval) clearInterval(this._errorsInterval);
                this._errorsInterval = setInterval(() => {
                    this.fetchErrors();
                    this.fetchErrorStats();
                }, 15000);
            } else {
                if (this._errorsInterval) {
                    clearInterval(this._errorsInterval);
                    this._errorsInterval = null;
                }
            }
        });
    };

    onCollapse = (collapsed) => {
        setNavCollapsed(collapsed);
        this.setState({ collapsed });
    };

    onRefresh = () => {
        this.setState({ infraLoading: true, errorsLoading: true });
        this.fetchInfra();
        this.fetchErrors();
        this.fetchErrorStats();
    };

    getFilteredLogs = () => {
        const { errors, logFilter } = this.state;
        if (logFilter === 'all') return errors;
        return errors.filter(e => {
            const lv = (e.level || 'info').toLowerCase();
            if (logFilter === 'warn') return lv === 'warn' || lv === 'warning';
            return lv === logFilter;
        });
    };

    /* ── 데스크톱 레이아웃 ─────────────────────────────────── */
    renderDesktop() {
        const {
            infraStatus,
            errorStats,
            logFilter,
            logLevel, logExchange, logSearch, allLogExchanges, autoRefresh,
        } = this.state;

        const adapters = infraStatus?.adapters || [];
        const activeCount = adapters.filter(a => a.active).length;
        const allOk = adapters.length > 0 && activeCount === adapters.length;
        const filteredLogs = this.getFilteredLogs();

        const byLevel = errorStats?.by_level || {};
        const byExchange = errorStats?.by_exchange || {};
        // 거래소별 분포: by_exchange 있으면 우선, 없으면 adapters fallback
        const distAdapters = Object.keys(byExchange).length > 0
            ? Object.entries(byExchange).map(([name, count]) => ({
                id: name, name,
                checkpoint_keys: count,
                active: adapters.some(a => (a.name || a.id) === name && a.active),
            }))
            : adapters;

        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

                {/* 파이프라인 시각화(1.2fr) + 거래소별 비중(1fr) */}
                <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 14 }}>

                    {/* 파이프라인 — SOURCE → NORMALIZER → STORAGE */}
                    <div style={{
                        padding: 18,
                        borderRadius: 16,
                        background: 'var(--color-surface-card)',
                        border: '1px solid var(--color-surface-border)',
                    }}>
                        <div style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            marginBottom: 16,
                        }}>
                            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-content)' }}>
                                수집 파이프라인
                            </span>
                            {/* 자동 갱신 표시 */}
                            <span style={{
                                display: 'inline-flex', alignItems: 'center', gap: 5,
                                padding: '3px 9px', borderRadius: 999,
                                border: '1px solid color-mix(in oklch, var(--color-gain) 40%, var(--color-surface-border))',
                                background: 'var(--color-gain-subtle)',
                                color: 'var(--color-gain)',
                                fontSize: 10, fontWeight: 600,
                                fontFamily: 'JetBrains Mono, monospace',
                            }}>
                                <V2Icon name="refresh" size={10} aria-hidden="true" />
                                자동 갱신
                            </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'stretch', gap: 8 }}>
                            {/* SOURCE */}
                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                                <div style={stageLabelStyle()}>SOURCE</div>
                                {adapters.length === 0 ? (
                                    <div style={{
                                        padding: 12, borderRadius: 10,
                                        background: 'var(--color-surface-card-alt)',
                                        border: '1px solid var(--color-surface-border)',
                                        fontSize: 12, color: 'var(--color-content-muted)',
                                        textAlign: 'center',
                                    }}>
                                        어댑터 없음
                                    </div>
                                ) : (
                                    <div role="list" aria-label="소스 어댑터 목록">
                                        {adapters.slice(0, 3).map(a => (
                                            <AdapterCard key={a.name || a.id} adapter={a} />
                                        ))}
                                        {adapters.length > 3 && (
                                            <div
                                                aria-label={`그 외 어댑터 ${adapters.length - 3}개`}
                                                style={{
                                                    fontFamily: 'JetBrains Mono, monospace',
                                                    fontSize: 11, color: 'var(--color-content-muted)',
                                                    textAlign: 'center', padding: '6px 0',
                                                }}
                                            >
                                                +{adapters.length - 3}개 더
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* 연결선 SOURCE → NORMALIZER */}
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <PipelineConnector
                                    height={Math.max(100, Math.min(adapters.length, 3) * 78)}
                                />
                            </div>

                            {/* NORMALIZER — TODO(DAS-65-G1): 단계별 상태 BE 확장 필요 */}
                            <div style={{
                                flex: 0.9,
                                display: 'flex', flexDirection: 'column',
                                justifyContent: 'center', gap: 8,
                            }}>
                                <div style={stageLabelStyle()}>NORMALIZER</div>
                                <PipelineStageBox
                                    label="Ticker Unifier"
                                    sublabel={allOk ? '정상' : '확인 필요'}
                                    tone={allOk ? 'gain' : 'warning'}
                                />
                                <PipelineStageBox
                                    label="Candle Builder"
                                    sublabel={allOk ? '정상' : '확인 필요'}
                                    tone={allOk ? 'gain' : 'warning'}
                                />
                            </div>

                            {/* 연결선 NORMALIZER → STORAGE */}
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <svg width={36} height={80} aria-hidden="true">
                                    <path
                                        d="M 2 40 L 34 40"
                                        stroke="var(--color-surface-border)"
                                        fill="none" strokeWidth={1.5}
                                    />
                                </svg>
                            </div>

                            {/* STORAGE — TODO(DAS-65-G1): TimescaleDB 용량 BE 메트릭 확장 필요 */}
                            <div style={{
                                flex: 0.7,
                                display: 'flex', flexDirection: 'column',
                                justifyContent: 'center', gap: 8,
                            }}>
                                <div style={stageLabelStyle()}>STORAGE</div>
                                <PipelineStageBox
                                    label="TimescaleDB"
                                    sublabel={allOk ? '정상' : '확인 필요'}
                                    tone={allOk ? 'gain' : 'warning'}
                                    iconName="wallet"
                                />
                            </div>
                        </div>
                    </div>

                    {/* 거래소별 수집 비중 */}
                    <div style={{
                        padding: 18,
                        borderRadius: 16,
                        background: 'var(--color-surface-card)',
                        border: '1px solid var(--color-surface-border)',
                    }}>
                        <div style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            marginBottom: 14,
                        }}>
                            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-content)' }}>
                                거래소별 수집 비중
                            </span>
                            <span style={{
                                fontFamily: 'JetBrains Mono, monospace',
                                fontSize: 11, color: 'var(--color-content-muted)',
                            }}>
                                최근 1시간
                            </span>
                        </div>
                        <AdapterDistributionChart adapters={distAdapters} />

                        {/* 에러 레벨 요약 배지 */}
                        {(byLevel.ERROR !== undefined || byLevel.WARN !== undefined || byLevel.INFO !== undefined) && (
                            <div style={{
                                marginTop: 16, paddingTop: 14,
                                borderTop: '1px solid var(--color-surface-border)',
                                display: 'flex', gap: 6, flexWrap: 'wrap',
                            }}>
                                {[
                                    { key: 'ERROR', tone: 'loss' },
                                    { key: 'WARN', tone: 'warning' },
                                    { key: 'INFO', tone: 'neutral' },
                                ].map(({ key, tone }) => byLevel[key] !== undefined ? (
                                    <V2Badge key={key} tone={tone}>
                                        {key} {byLevel[key].toLocaleString()}
                                    </V2Badge>
                                ) : null)}
                            </div>
                        )}
                    </div>
                </div>

                {/* 이벤트 스트림(1fr) + 로그 상세(1.3fr) */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.3fr', gap: 14 }}>

                    {/* 이벤트 스트림 */}
                    <div style={{
                        padding: 18,
                        borderRadius: 16,
                        background: 'var(--color-surface-card)',
                        border: '1px solid var(--color-surface-border)',
                    }}>
                        <div style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            marginBottom: 12,
                        }}>
                            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-content)' }}>
                                이벤트 스트림
                            </span>
                            <span style={{
                                display: 'inline-flex', alignItems: 'center', gap: 5,
                                fontSize: 11, color: 'var(--color-content-muted)',
                            }}>
                                <V2Icon name="filter" size={12} aria-hidden="true" />
                                필터
                            </span>
                        </div>
                        {filteredLogs.length === 0 ? (
                            <div style={{
                                padding: '32px 0', textAlign: 'center',
                                fontSize: 12, color: 'var(--color-content-muted)',
                            }}>
                                이벤트 없음
                            </div>
                        ) : (
                            filteredLogs.slice(0, 6).map((log, i) => (
                                <EventRow key={log.id || i} log={log} />
                            ))
                        )}
                    </div>

                    {/* 로그 상세 테이블 */}
                    <div style={{
                        padding: 18,
                        borderRadius: 16,
                        background: 'var(--color-surface-card)',
                        border: '1px solid var(--color-surface-border)',
                    }}>
                        <div style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            marginBottom: 10, flexWrap: 'wrap', gap: 8,
                        }}>
                            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-content)' }}>
                                로그 상세
                            </span>
                            {/* 자동 갱신 pill — 시안 V2 "error / warn on / info" 패턴 */}
                            <div style={{ display: 'flex', gap: 5 }}>
                                {[
                                    { key: 'error', label: 'error', tone: 'loss' },
                                    { key: 'warn', label: 'warn', tone: 'warning' },
                                    { key: 'info', label: 'info', tone: 'neutral' },
                                ].map(({ key, label, tone }) => {
                                    const active = logFilter === key;
                                    const colors = {
                                        loss: { bg: 'var(--color-loss-subtle)', color: 'var(--color-loss)' },
                                        warning: { bg: 'var(--color-warning-subtle)', color: 'var(--color-warning)' },
                                        neutral: { bg: 'var(--color-surface-elevated)', color: 'var(--color-content-secondary)' },
                                    };
                                    const c = colors[tone] || colors.neutral;
                                    return (
                                        <button
                                            key={key}
                                            type="button"
                                            onClick={() => this.setState({ logFilter: active ? 'all' : key })}
                                            aria-pressed={active}
                                            style={{
                                                padding: '2px 8px', borderRadius: 999,
                                                border: `1px solid ${active ? c.color : 'var(--color-surface-border)'}`,
                                                background: active ? c.bg : 'transparent',
                                                color: active ? c.color : 'var(--color-content-muted)',
                                                fontSize: 10, fontWeight: 600,
                                                fontFamily: 'JetBrains Mono, monospace',
                                                cursor: 'pointer', minHeight: 22,
                                            }}
                                        >
                                            {label}
                                        </button>
                                    );
                                })}
                                {/* 자동 갱신 토글 */}
                                <button
                                    type="button"
                                    onClick={this.toggleAutoRefresh}
                                    aria-pressed={autoRefresh}
                                    // 접근성 이름은 보이는 글자를 포함해야 한다 (WCAG 2.5.3 Label in Name) — 종전에는
                                // 보이는 글자가 '자동 갱신 중지됨'인데 이름은 '자동 갱신 시작'이라 어긋났다 (DAS-176)
                                aria-label={autoRefresh ? '자동 갱신 중 — 클릭하면 중지' : '자동 갱신 중지됨 — 클릭하면 시작'}
                                    style={{
                                        padding: '2px 8px', borderRadius: 999,
                                        border: autoRefresh
                                            ? '1px solid color-mix(in oklch, var(--color-gain) 40%, var(--color-surface-border))'
                                            : '1px solid var(--color-surface-border)',
                                        background: autoRefresh ? 'var(--color-gain-subtle)' : 'transparent',
                                        color: autoRefresh ? 'var(--color-gain)' : 'var(--color-content-muted)',
                                        fontSize: 10, fontWeight: 600,
                                        fontFamily: 'JetBrains Mono, monospace',
                                        cursor: 'pointer', minHeight: 22,
                                    }}
                                >
                                    {autoRefresh ? '자동 갱신 중' : '중지됨'}
                                </button>
                            </div>
                        </div>

                        {/* 서버 측 필터 바 */}
                        <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
                            <select
                                value={logLevel}
                                onChange={e => this.onLogFilterChange('logLevel', e.target.value)}
                                aria-label="레벨 필터"
                                style={{
                                    padding: '4px 8px', borderRadius: 8,
                                    border: '1px solid var(--color-surface-border)',
                                    background: 'var(--color-surface)',
                                    color: 'var(--color-content)',
                                    fontSize: 11,
                                    fontFamily: 'JetBrains Mono, monospace',
                                    cursor: 'pointer',
                                }}
                            >
                                <option value="">전체 레벨</option>
                                <option value="ERROR">ERROR</option>
                                <option value="WARN">WARN</option>
                                <option value="INFO">INFO</option>
                            </select>
                            <select
                                value={logExchange}
                                onChange={e => this.onLogFilterChange('logExchange', e.target.value)}
                                aria-label="거래소 필터"
                                style={{
                                    padding: '4px 8px', borderRadius: 8,
                                    border: '1px solid var(--color-surface-border)',
                                    background: 'var(--color-surface)',
                                    color: 'var(--color-content)',
                                    fontSize: 11,
                                    fontFamily: 'JetBrains Mono, monospace',
                                    cursor: 'pointer',
                                }}
                            >
                                <option value="">전체 거래소</option>
                                {allLogExchanges.map(ex => (
                                    <option key={ex} value={ex}>{ex}</option>
                                ))}
                            </select>
                            <input
                                type="text"
                                placeholder="메시지 검색..."
                                value={logSearch}
                                onChange={e => this.onLogFilterChange('logSearch', e.target.value)}
                                aria-label="메시지 검색"
                                style={{
                                    flex: 1, minWidth: 100,
                                    padding: '4px 10px', borderRadius: 8,
                                    border: '1px solid var(--color-surface-border)',
                                    background: 'var(--color-surface)',
                                    color: 'var(--color-content)',
                                    fontSize: 11, outline: 'none',
                                    fontFamily: 'JetBrains Mono, monospace',
                                }}
                            />
                        </div>

                        {/* 테이블 */}
                        {filteredLogs.length === 0 ? (
                            <div style={{
                                padding: '32px 0', textAlign: 'center',
                                fontSize: 12, color: 'var(--color-content-muted)',
                            }}>
                                로그 없음
                            </div>
                        ) : (
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr>
                                        {['시간', '레벨', '심볼', '메시지'].map(th => (
                                            <th key={th} style={{
                                                textAlign: 'left', padding: '0 4px 8px',
                                                fontSize: 10, fontWeight: 600,
                                                letterSpacing: '0.08em', textTransform: 'uppercase',
                                                color: 'var(--color-content-muted)',
                                                fontFamily: 'JetBrains Mono, monospace',
                                                borderBottom: '1px solid var(--color-surface-border)',
                                            }}>
                                                {th}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredLogs.map((log, i) => (
                                        <LogTableRow key={log.id || i} log={log} />
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    /* ── 모바일: 시안 V2 BacktestMobileV2 정합 ─────────────── */
    renderMobile() {
        const { infraStatus, errorStats, logFilter } = this.state;
        const adapters = infraStatus?.adapters || [];
        const activeCount = adapters.filter(a => a.active).length;
        const allOk = adapters.length > 0 && activeCount === adapters.length;
        const filteredLogs = this.getFilteredLogs();

        const FILTER_BTNS = [
            { key: 'all', label: 'ALL' },
            { key: 'error', label: 'E' },
            { key: 'warn', label: 'W' },
            { key: 'info', label: 'I' },
        ];

        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {/* Hero KPI */}
                <HeroStrip infraStatus={infraStatus} errorStats={errorStats} isMobile />

                {/* 수집기 2-col grid */}
                {adapters.length > 0 && (
                    <div style={{
                        padding: 16,
                        borderRadius: 14,
                        background: 'var(--color-surface-card)',
                        border: '1px solid var(--color-surface-border)',
                    }}>
                        <div style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            marginBottom: 12,
                        }}>
                            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-content)' }}>
                                수집기
                            </span>
                            <V2Badge tone={allOk ? 'gain' : 'warning'}>
                                {activeCount}/{adapters.length}
                            </V2Badge>
                        </div>
                        <div
                            role="list"
                            aria-label="수집기 목록"
                            style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(2, 1fr)',
                                gap: 8,
                            }}
                        >
                            {adapters.map(a => (
                                <AdapterCard key={a.name || a.id} adapter={a} />
                            ))}
                        </div>
                    </div>
                )}

                {/* 이벤트 스트림 */}
                <div style={{
                    padding: 16,
                    borderRadius: 14,
                    background: 'var(--color-surface-card)',
                    border: '1px solid var(--color-surface-border)',
                }}>
                    <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        marginBottom: 12,
                    }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-content)' }}>
                            이벤트
                        </span>
                        {/* 레벨 E/W/I pill — 시안 V2 mobile */}
                        <div role="group" aria-label="이벤트 레벨 필터" style={{ display: 'flex', gap: 4 }}>
                            {FILTER_BTNS.map(btn => {
                                const isActive = logFilter === btn.key;
                                const isError = btn.key === 'error';
                                const isWarn = btn.key === 'warn';
                                const activeBg = isError ? 'var(--color-loss-subtle)'
                                    : isWarn ? 'var(--color-warning-subtle)'
                                    : 'var(--color-surface-elevated)';
                                const activeColor = isError ? 'var(--color-loss)'
                                    : isWarn ? 'var(--color-warning)'
                                    : 'var(--color-content-secondary)';
                                return (
                                    <button
                                        key={btn.key}
                                        type="button"
                                        onClick={() => this.setState({ logFilter: (btn.key !== 'all' && isActive) ? 'all' : btn.key })}
                                        aria-pressed={isActive}
                                        style={{
                                            padding: '3px 8px', borderRadius: 999,
                                            border: `1px solid ${isActive ? activeColor : 'var(--color-surface-border)'}`,
                                            background: isActive ? activeBg : 'transparent',
                                            color: isActive ? activeColor : 'var(--color-content-muted)',
                                            fontSize: 10, fontWeight: 600,
                                            fontFamily: 'JetBrains Mono, monospace',
                                            cursor: 'pointer', minHeight: 24,
                                        }}
                                    >
                                        {btn.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    {filteredLogs.length === 0 ? (
                        <div style={{
                            padding: '24px 0', textAlign: 'center',
                            fontSize: 12, color: 'var(--color-content-muted)',
                        }}>
                            이벤트 없음
                        </div>
                    ) : (
                        filteredLogs.slice(0, 8).map((log, i) => (
                            <EventRow key={log.id || i} log={log} />
                        ))
                    )}
                </div>
            </div>
        );
    }

    render() {
        const { collapsed, isMobile, infraLoading, infraError, infraStatus, errorStats } = this.state;

        return (
            <div className="min-h-screen w-full bg-surface text-content sm:overflow-x-auto">
                <NavBar collapsed={collapsed} onCollapse={this.onCollapse} />
                <main
                    className={`min-w-0 w-full pt-12 pb-16 sm:pt-0 sm:pb-0 transition-[padding-left] duration-200 ${
                        collapsed ? 'sm:pl-[56px]' : 'sm:pl-[220px]'
                    }`}
                    style={{ marginTop: 'var(--maintenance-banner-h, 0px)' }}
                >
                    <MaintenanceBanner />
                    <div className="mx-3 my-3 min-w-0 sm:mx-6 sm:my-5">

                        {/* 페이지 헤더 — 시안 V2 TopBar(Data Ops + subtitle) */}
                        <div style={{
                            display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
                            marginBottom: 20, flexWrap: 'wrap', gap: 10,
                        }}>
                            <div>
                                <div style={{ fontSize: 12, color: 'var(--color-content-secondary)', marginBottom: 4 }}>
                                    백테스트 · 수집 모니터링
                                </div>
                                <h1 style={{
                                    fontSize: 20, fontWeight: 700,
                                    color: 'var(--color-content)',
                                    letterSpacing: '-0.02em', margin: 0,
                                }}>
                                    Data Ops
                                </h1>
                                <div style={{ fontSize: 12, color: 'var(--color-content-muted)', marginTop: 2 }}>
                                    수집기 파이프라인 · 이벤트 · 로그를 한 화면에서 운영
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={this.onRefresh}
                                aria-label="새로고침"
                                className="inline-flex items-center gap-1.5 rounded-lg border border-surface-border bg-surface-card px-3 py-1.5 text-xs font-semibold text-content-secondary hover:bg-surface-hover transition-colors"
                                style={{ minHeight: 32 }}
                            >
                                <V2Icon name="refresh" size={12} aria-hidden="true" />
                                <span className="hidden sm:inline">새로고침</span>
                            </button>
                        </div>

                        {/* 로딩 */}
                        {infraLoading && (
                            <div style={{
                                padding: 20, borderRadius: 14,
                                background: 'var(--color-surface-card)',
                                border: '1px solid var(--color-surface-border)',
                                fontSize: 13, color: 'var(--color-content-secondary)',
                                marginBottom: 16,
                            }}>
                                파이프라인 상태를 불러오는 중...
                            </div>
                        )}

                        {/* 연결 실패 배너 */}
                        {infraError && !infraLoading && (
                            <div style={{
                                padding: 16, borderRadius: 14,
                                background: 'var(--color-loss-subtle)',
                                border: '1px solid color-mix(in oklch, var(--color-loss) 30%, var(--color-surface-border))',
                                display: 'flex', alignItems: 'center', gap: 10,
                                marginBottom: 16,
                            }}>
                                <StatusDot tone="loss" size={8} />
                                <span style={{ fontSize: 13, color: 'var(--color-loss)', fontWeight: 600 }}>
                                    BacktestInfra에 연결할 수 없습니다
                                </span>
                                <span style={{ fontSize: 12, color: 'var(--color-content-muted)' }}>
                                    {infraError}
                                </span>
                            </div>
                        )}

                        {/* Hero KPI 스트립 — 데스크톱 전용 (모바일은 renderMobile 내부에서 렌더) */}
                        {!infraLoading && !infraError && !isMobile && (
                            <div style={{ marginBottom: 16 }}>
                                <HeroStrip infraStatus={infraStatus} errorStats={errorStats} isMobile={false} />
                            </div>
                        )}

                        {/* 메인 컨텐츠 */}
                        {!infraLoading && (
                            isMobile ? this.renderMobile() : this.renderDesktop()
                        )}
                    </div>
                </main>
                <ScrollToTopButton />
            </div>
        );
    }
}

export default BacktestPageV3;
